/*
 * Copyright (c) 2024-2025 Nodals AI Ltd
 * All rights reserved.
 */

import { Logger } from '../../../logger';
import { generateUUID } from '../../../util';
import { SiteConfig } from '../../config';
import { BaseVideoPlacement, Quartile } from './index';

import type { CampaignData, RenderedAdData } from '../../../types/index';
import type {
  GenericPrebidVideoAdEvent,
  PrebidAdUnit,
  PrebidApi,
  PrebidVideoAdStartedData,
  PrebidVideoAdTimeData,
  PrebidVideoPlayerResizeData,
} from '../../../types/prebid';
import { getGlobalPrebidObj } from '../../../util';

const logger = Logger.getInstance();

class PrebidVideoPlacement extends BaseVideoPlacement {
  private pbApi: PrebidApi | undefined = undefined;

  constructor(
    adUnit: PrebidAdUnit,
    adUnitTransformer: (adUnitCode: string) => string,
    campaignData: CampaignData[],
    siteConfig: SiteConfig
  ) {
    super(adUnit, adUnitTransformer, campaignData, siteConfig);
  }

  protected setSlot(): void {
    if (this.slot !== null) {
      return;
    }
    this.pbApi = getGlobalPrebidObj();
    if (!this.pbApi) {
      logger.warn('Global Prebid object is not available on the page');
      return;
    }
    const adUnitVideoConfig = this.adUnit?.video;
    if (!adUnitVideoConfig?.divId) {
      logger.warn(`No divId found in adUnit video config for ${this.adUnitCode}`);
      return;
    }
    const slotElement = document.getElementById(adUnitVideoConfig.divId);
    if (!slotElement) {
      logger.warn(`No slot element found for divId ${adUnitVideoConfig.divId}`);
      return;
    }
    this.slot = slotElement;
  }

  protected setSupportedSizes(): void {}

  protected determineVisibility(): void {
    const size = this.getSmallestSupportedSize();
    if (!size || this.slot === null) {
      return;
    }

    const isVisible = this.checkElementVisibility(this.slot, size);
    this.isVisible(isVisible);
  }

  protected setWrappingDomElement(): void {
    if (this.slot) {
      this.wrappingDomElement = this.slot;
      this.playerWidth = this.slot.clientWidth;
      this.playerHeight = this.slot.clientHeight;
    }
  }

  protected addListeners(): void {
    if (!this.pbApi) {
      return;
    }
    this.pbApi.onEvent('videoPlayerResize', (event: PrebidVideoPlayerResizeData) =>
      this.playerResizeHandler(event)
    );
    this.pbApi.onEvent('videoAdStarted', (event: PrebidVideoAdStartedData) =>
      this.adStartedHandler(event)
    );
    this.pbApi.onEvent('videoAdSkipped', (event: GenericPrebidVideoAdEvent) =>
      this.adSkippedHandler(event)
    );
    this.pbApi.onEvent('videoAdTime', (event: PrebidVideoAdTimeData) =>
      this.adProgressHandler(event)
    );
    this.pbApi.onEvent('videoAdImpression', (event: GenericPrebidVideoAdEvent) =>
      this.adImpressionHandler(event)
    );
    this.pbApi.onEvent('videoAdComplete', (event: GenericPrebidVideoAdEvent) =>
      this.adCompleteHandler(event)
    );
  }

  protected addAdInteractionListener(): void {
    if (!this.pbApi) {
      return;
    }
    this.pbApi.onEvent('videoAdClick', () => this.videoAdClickHandler());
  }

  private adStartedHandler(event: PrebidVideoAdStartedData): void {
    if (this.wrappingDomElement?.id !== event.divId || this.adCurrentlyPlaying) {
      return;
    }

    logger.debug(
      `Ad Started event received for ${this.adUnitCode}, data: ${JSON.stringify(event)}`
    );

    this.adCurrentlyPlaying = true;

    const adId =
      event.wrapperAdIds.length > 0
        ? event.wrapperAdIds[event.wrapperAdIds.length - 1] // Outermost ad ID.
        : event.adId;

    const renderedAdData: RenderedAdData = {
      advertiserId: event.advertiserId || undefined,
      lineItemId: adId || undefined,
      creativeId: event.creativeId || undefined,
      isPrebidAd: false, // How to determine this?
      isProgrammaticBuy: false, // How to determine this?
      responseIdentifier: generateUUID(),
      size: { width: this.playerWidth, height: this.playerHeight },
    };
    this.handleRenderedAdData(renderedAdData);
  }

  private playerResizeHandler(event: PrebidVideoPlayerResizeData): void {
    if (this.wrappingDomElement?.id !== event.divId) {
      return;
    }
    this.playerWidth = event.width;
    this.playerHeight = event.height;
  }

  private adImpressionHandler(event: GenericPrebidVideoAdEvent): void {
    if (this.wrappingDomElement?.id !== event.divId) {
      return;
    }
    this.dispatchViewableImpression();
  }

  private videoAdClickHandler(): void {
    if (!this.adCurrentlyPlaying) {
      return;
    }
    this.dispatchAdInteraction();
  }

  private adSkippedHandler(event: GenericPrebidVideoAdEvent): void {
    if (this.wrappingDomElement?.id !== event.divId) {
      return;
    }
    this.adCurrentlyPlaying = false;
  }

  private adCompleteHandler(event: GenericPrebidVideoAdEvent): void {
    if (this.wrappingDomElement?.id !== event.divId || this.adCompleteFiredForOpportunity) {
      return;
    }
    this.dispatchAdQuartileEvent(Quartile.FOURTH);
    this.adCurrentlyPlaying = false;
    this.adCompleteFiredForOpportunity = true;
  }

  private adProgressHandler(data: PrebidVideoAdTimeData): void {
    const delta = 1;

    const progress = (data.time / data.duration) * 100 + delta;
    if (progress >= 75 && !this.adQuartilesFiredForOpportunity.THIRD) {
      this.adQuartilesFiredForOpportunity.THIRD = true;
      this.dispatchAdQuartileEvent(Quartile.THIRD);
    } else if (progress >= 50 && !this.adQuartilesFiredForOpportunity.SECOND) {
      this.adQuartilesFiredForOpportunity.SECOND = true;
      this.dispatchAdQuartileEvent(Quartile.SECOND);
    } else if (progress >= 25 && !this.adQuartilesFiredForOpportunity.FIRST) {
      this.adQuartilesFiredForOpportunity.FIRST = true;
      this.dispatchAdQuartileEvent(Quartile.FIRST);
    }
  }
}

export { PrebidVideoPlacement };
